import axios from "axios";
import { auth } from "@/lib/firebase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/mailing`;

const headers = async () => {
  if (!auth.currentUser) throw new Error("Missing Firebase admin session");
  const token = await auth.currentUser.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

const authed = async (cfg = {}) => ({ ...cfg, headers: { ...(cfg.headers || {}), ...(await headers()) } });

export const formatApiError = (err, fallback = "Request failed") => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return null;
        const loc = Array.isArray(item.loc) ? item.loc.filter((x) => x !== "body").join(".") : "";
        const msg = item.msg || item.type;
        return loc && msg ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return messages.length ? messages.join("; ") : fallback;
  }

  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  return err?.message || fallback;
};

export const mailingApi = {
  status: async () => (await axios.get(`${API}/status`, await authed())).data,
  settings: {
    get: async () => (await axios.get(`${API}/settings`, await authed())).data,
    update: async (patch) => (await axios.patch(`${API}/settings`, patch, await authed())).data,
  },
  contacts: {
    list: async (params = {}) =>
      (await axios.get(`${API}/contacts`, await authed({ params }))).data,
    create: async (body) => (await axios.post(`${API}/contacts`, body, await authed())).data,
    update: async (id, patch) =>
      (await axios.patch(`${API}/contacts/${id}`, patch, await authed())).data,
    remove: async (id) => (await axios.delete(`${API}/contacts/${id}`, await authed())).data,
    importCsv: async (file, source = "csv_import") => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      const cfg = await authed();
      cfg.headers = { ...cfg.headers, "Content-Type": "multipart/form-data" };
      return (await axios.post(`${API}/contacts/import`, fd, cfg)).data;
    },
    count: async (params = {}) =>
      (await axios.get(`${API}/contacts/count`, await authed({ params }))).data,
    bulkDelete: async (ids) =>
      (await axios.post(`${API}/contacts/bulk-delete`, { ids }, await authed())).data,
    deleteAll: async ({ status = null, tag = null } = {}) =>
      (await axios.post(`${API}/contacts/delete-all`, { status, tag, confirm: true }, await authed())).data,
    bulkStatus: async (ids, status) =>
      (await axios.post(`${API}/contacts/bulk-status`, { ids, status }, await authed())).data,
    bulkTag: async (ids, tag) =>
      (await axios.post(`${API}/contacts/bulk-tag`, { ids, tag }, await authed())).data,
    exportCsv: async (params = {}) => {
      const cfg = await authed({ params, responseType: "blob" });
      return (await axios.get(`${API}/contacts/export`, cfg)).data;
    },
  },
  templates: {
    list: async () => (await axios.get(`${API}/templates`, await authed())).data,
    get: async (id) => (await axios.get(`${API}/templates/${id}`, await authed())).data,
    create: async (body) => (await axios.post(`${API}/templates`, body, await authed())).data,
    update: async (id, patch) =>
      (await axios.patch(`${API}/templates/${id}`, patch, await authed())).data,
    remove: async (id) => (await axios.delete(`${API}/templates/${id}`, await authed())).data,
    preview: async (campaign) =>
      (await axios.post(`${API}/preview`, campaign, await authed())).data,
  },
  uploads: {
    attachment: async (file, ownerId = "draft") => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_id", ownerId);
      const cfg = await authed();
      cfg.headers = { ...cfg.headers, "Content-Type": "multipart/form-data" };
      return (await axios.post(`${API}/uploads`, fd, cfg)).data;
    },
    image: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const cfg = await authed();
      cfg.headers = { ...cfg.headers, "Content-Type": "multipart/form-data" };
      return (await axios.post(`${API}/uploads/image`, fd, cfg)).data;
    },
  },
  campaigns: {
    list: async () => (await axios.get(`${API}/campaigns`, await authed())).data,
    get: async (id) => (await axios.get(`${API}/campaigns/${id}`, await authed())).data,
    create: async (body) => (await axios.post(`${API}/campaigns`, body, await authed())).data,
    update: async (id, patch) =>
      (await axios.patch(`${API}/campaigns/${id}`, patch, await authed())).data,
    remove: async (id) => (await axios.delete(`${API}/campaigns/${id}`, await authed())).data,
    jobs: async (id, params = {}) =>
      (await axios.get(`${API}/campaigns/${id}/jobs`, await authed({ params }))).data,
    start: async (id, scheduledFor = null) =>
      (await axios.post(`${API}/campaigns/${id}/start`, { scheduled_for: scheduledFor }, await authed())).data,
    pause: async (id) => (await axios.post(`${API}/campaigns/${id}/pause`, {}, await authed())).data,
    resume: async (id) => (await axios.post(`${API}/campaigns/${id}/resume`, {}, await authed())).data,
    cancel: async (id) => (await axios.post(`${API}/campaigns/${id}/cancel`, {}, await authed())).data,
    testSend: async (id, toEmail) =>
      (await axios.post(`${API}/campaigns/${id}/test-send`, { to_email: toEmail }, await authed())).data,
  },
};
